import {
  BaseCheckpointSaver,
  Checkpoint,
  CheckpointMetadata,
  CheckpointTuple,
} from "@langchain/langgraph";
import type { RunnableConfig } from "@langchain/core/runnables";
import {
  MemoryClient,
  IMemoryClientOptions,
  Order,
} from "@cloudbase/agent-agents";

// Define types locally if not available from langgraph
type PendingWrite = [string, any];
type CheckpointPendingWrite = [string, string, any];

interface CheckpointListOptions {
  limit?: number;
  before?: RunnableConfig;
  filter?: Record<string, any>;
}

export interface TDAISaverConfig extends IMemoryClientOptions {
  checkpointType?: string;
  checkpointWritesType?: string;
}

/**
 * TDAISaver - LangGraph checkpoint saver implementation using TDAI Memory
 *
 * Storage Strategy:
 * - Events (NoSQL): Store checkpoint data and pending writes in separate collections
 * - Supports namespaces and parent checkpoint relationships
 */
export class TDAISaver extends BaseCheckpointSaver {
  private memoryClient: MemoryClient;
  private checkpointType: string;
  private checkpointWritesType: string;

  constructor(config: TDAISaverConfig) {
    super();
    const {
      checkpointType = "checkpoints",
      checkpointWritesType = "checkpoint_writes",
      ...clientConfig
    } = config;

    this.memoryClient = new MemoryClient(clientConfig);
    this.checkpointType = checkpointType;
    this.checkpointWritesType = checkpointWritesType;
  }

  /**
   * Retrieves a checkpoint from TDAI Memory based on the provided config.
   * If the config contains a "checkpoint_id" key, the checkpoint with the matching
   * thread ID and checkpoint ID is retrieved. Otherwise, the latest checkpoint
   * for the given thread ID is retrieved.
   */
  async getTuple(config: RunnableConfig): Promise<CheckpointTuple | undefined> {
    try {
      const {
        thread_id,
        checkpoint_ns = "",
        checkpoint_id,
      } = config.configurable ?? {};

      if (!thread_id) {
        return undefined;
      }

      const query: Record<string, any> = {
        collection: this.checkpointType,
        checkpoint_ns,
      };

      if (checkpoint_id) {
        query.checkpoint_id = checkpoint_id;
      }

      // Query events for checkpoint data
      const { events = [] } = await this.memoryClient.queryEvents({
        sessionId: thread_id,
        where: query,
        orderBy: { checkpoint_id: Order.DESCENDING },
        limit: 1,
      });

      if (events.length === 0) {
        return undefined;
      }

      const doc = events[0];
      const configurableValues = {
        checkpoint_ns,
        checkpoint_id: doc.checkpoint_id,
      };

      // Get checkpoint data (stored as JSON)
      const checkpoint: Checkpoint = doc.checkpoint;

      // Get pending writes
      const { events: serializedWrites = [] } =
        await this.memoryClient.queryEvents({
          sessionId: thread_id,
          where: {
            collection: this.checkpointWritesType,
            ...configurableValues,
          },
        });

      const pendingWrites: CheckpointPendingWrite[] = serializedWrites.map(
        (serializedWrite: any) => {
          return [
            serializedWrite.task_id,
            serializedWrite.channel,
            serializedWrite.value,
          ] as CheckpointPendingWrite;
        }
      );

      // Get metadata (stored as JSON)
      const metadata: CheckpointMetadata = doc.metadata || {};

      return {
        config: { configurable: configurableValues },
        checkpoint,
        pendingWrites,
        metadata,
        parentConfig:
          doc.parent_checkpoint_id != null
            ? {
                configurable: {
                  thread_id,
                  checkpoint_ns,
                  checkpoint_id: doc.parent_checkpoint_id,
                },
              }
            : undefined,
      };
    } catch (error) {
      console.error("Error getting checkpoint:", error);
      return undefined;
    }
  }

  /**
   * Retrieve a list of checkpoint tuples from TDAI Memory based on the provided config.
   * The checkpoints are ordered by checkpoint ID in descending order (newest first).
   */
  async *list(
    config: RunnableConfig,
    options?: CheckpointListOptions
  ): AsyncGenerator<CheckpointTuple> {
    const { limit, before, filter } = options ?? {};

    if (!config?.configurable?.thread_id) {
      throw new Error("Thread ID is required");
    }

    const query: Record<string, any> = {
      collection: this.checkpointType,
    };

    if (
      config?.configurable?.checkpoint_ns !== undefined &&
      config?.configurable?.checkpoint_ns !== null
    ) {
      query.checkpoint_ns = config.configurable.checkpoint_ns;
    }

    // Apply metadata filters
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        query[`metadata.${key}`] = value;
      });
    }

    // Apply before filter
    if (before) {
      query.checkpoint_id = { $lt: before.configurable?.checkpoint_id };
    }

    // Query events
    const { events = [] } = await this.memoryClient.queryEvents({
      sessionId: config?.configurable?.thread_id || "default",
      where: query,
      orderBy: { checkpoint_id: Order.DESCENDING },
      limit: limit,
    });

    for (const doc of events) {
      // Get checkpoint and metadata (stored directly without serialization)
      const checkpoint: Checkpoint = doc.checkpoint;
      const metadata: CheckpointMetadata = doc.metadata || {};

      yield {
        config: {
          configurable: {
            thread_id: doc.thread_id,
            checkpoint_ns: doc.checkpoint_ns,
            checkpoint_id: doc.checkpoint_id,
          },
        },
        checkpoint,
        metadata,
        parentConfig: doc.parent_checkpoint_id
          ? {
              configurable: {
                thread_id: doc.thread_id,
                checkpoint_ns: doc.checkpoint_ns,
                checkpoint_id: doc.parent_checkpoint_id,
              },
            }
          : undefined,
      };
    }
  }

  /**
   * Saves a checkpoint to TDAI Memory. The checkpoint is associated with the
   * provided config and its parent config (if any).
   */
  async put(
    config: RunnableConfig,
    checkpoint: Checkpoint,
    metadata: CheckpointMetadata
  ): Promise<RunnableConfig> {
    try {
      const thread_id = config.configurable?.thread_id;
      const checkpoint_ns = config.configurable?.checkpoint_ns ?? "";
      const checkpoint_id = checkpoint.id;

      if (thread_id === undefined) {
        throw new Error(
          `The provided config must contain a configurable field with a "thread_id" field.`
        );
      }

      // Prepare document for storage (store as JSON)
      const doc = {
        collection: this.checkpointType,
        checkpoint_ns,
        checkpoint_id,
        parent_checkpoint_id: config.configurable?.checkpoint_id,
        checkpoint: checkpoint,
        metadata: metadata,
      };

      const { events = [] } = await this.memoryClient.queryEvents({
        sessionId: thread_id,
        where: {
          collection: this.checkpointType,
          checkpoint_ns,
          checkpoint_id,
        },
      });

      if (events[0]) {
        this.memoryClient.deleteEvent({
          sessionId: thread_id,
          eventId: events[0].id,
          //   messages: doc,
        });
      } else {
        await this.memoryClient.appendEvent({
          sessionId: thread_id,
          messages: doc,
        });
      }

      return {
        configurable: {
          thread_id,
          checkpoint_ns,
          checkpoint_id,
        },
      };
    } catch (error) {
      console.error("Error saving checkpoint:", error);
      throw error;
    }
  }

  /**
   * Saves intermediate writes associated with a checkpoint to TDAI Memory.
   */
  async putWrites(
    config: RunnableConfig,
    writes: PendingWrite[],
    taskId: string
  ): Promise<void> {
    try {
      const thread_id = config.configurable?.thread_id;
      const checkpoint_ns = config.configurable?.checkpoint_ns;
      const checkpoint_id = config.configurable?.checkpoint_id;

      if (
        thread_id === undefined ||
        checkpoint_ns === undefined ||
        checkpoint_id === undefined
      ) {
        throw new Error(
          `The provided config must contain a configurable field with "thread_id", "checkpoint_ns" and "checkpoint_id" fields.`
        );
      }

      // Process writes in parallel
      const writePromises = writes.map(async ([channel, value], idx) => {
        const writeDoc = {
          collection: this.checkpointWritesType,
          checkpoint_ns,
          checkpoint_id,
          task_id: taskId,
          idx,
          channel,
          value: value, // Store directly as JSON
        };

        return this.memoryClient.appendEvent({
          sessionId: thread_id,
          messages: writeDoc,
        });
      });

      await Promise.all(writePromises);
    } catch (error) {
      console.error("Error storing writes:", error);
      throw error;
    }
  }

  /**
   * Delete all checkpoints and writes for a thread from TDAI Memory.
   */
  async deleteThread(threadId: string): Promise<void> {
    try {
      // Delete the entire session which will remove all data
      await this.memoryClient.deleteSession({
        sessionId: threadId,
      });
    } catch (error) {
      console.error("Error deleting thread:", error);
      throw error;
    }
  }

  /**
   * Close the memory client connection
   */
  close(): void {
    this.memoryClient.close();
  }
}

